import {
  ClipboardList,
  FileText,
  Folder,
  Headphones,
  Image as ImageIcon,
  LayoutTemplate,
  MessageSquare,
  RotateCcw,
} from "lucide-react"

import { cn } from "@/lib/utils"

// Tab key -> label + glyph. The controller emits the ordered key list per order
// type (OrderDetailPresenter::TABS_BY_TYPE); the meaning lives here so the
// presenter stays free of view concerns.
export const TAB_META = {
  document: { label: "Document", Icon: FileText },
  chat: { label: "Chat", Icon: MessageSquare },
  revisions: { label: "Revision Requests", Icon: RotateCcw },
  details: { label: "Order Details", Icon: ClipboardList },
  files: { label: "Shared Files", Icon: Folder },
  design: { label: "Design Preview", Icon: ImageIcon },
  illus: { label: "Illustrations", Icon: ImageIcon },
  proof: { label: "Formatted Proof", Icon: LayoutTemplate },
  audio: { label: "Audio", Icon: Headphones },
}

// Vertical tab rail in a card (design prototype `.detail-nav`). Switching is
// client-side; the parent mirrors the active key into ?tab= so each tab stays
// linkable and refresh-safe. A dot marks the lead tab when the order needs the
// customer's attention (primaryDot).
//
// Below 860 (Switchback, P8-H3) the rail turns into a full-width horizontal
// strip that pans: the tabs stop shrinking so their labels stay legible, and
// the strip scrolls sideways instead — nine tabs never fit a phone. The
// scrollbar is hidden the same way the Projects filter chips hide theirs.
export default function OrderTabs({ tabs = [], active, onSelect, primaryDot = false, badges = {} }) {
  return (
    <aside className="w-[212px] shrink-0 max-[860px]:w-full">
      <div className="rounded-2xl border border-border bg-card p-2.5 max-[860px]:p-1.5">
        <nav className="flex flex-col gap-0.5 max-[860px]:flex-row max-[860px]:overflow-x-auto max-[860px]:[scrollbar-width:none] max-[860px]:[&::-webkit-scrollbar]:hidden">
          {tabs.map((key, i) => {
            const meta = TAB_META[key]
            if (!meta) return null

            const { label, Icon } = meta
            const isActive = key === active
            const showDot = i === 0 && primaryDot
            const badge = Number(badges[key]) || 0

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex items-center gap-[11px] rounded-[9px] px-[11px] py-[9px] text-[13px] font-medium transition-colors",
                  "max-[860px]:shrink-0 max-[860px]:px-[13px] max-[860px]:whitespace-nowrap",
                  isActive
                    ? "bg-[var(--red-soft)] font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                <Icon
                  className={cn(
                    "size-[17px] shrink-0",
                    isActive ? "text-primary" : "text-ink-3"
                  )}
                  strokeWidth={1.85}
                />
                <span className="truncate">{label}</span>
                {badge > 0 && (
                  <span
                    aria-label={`${badge} unread message${badge === 1 ? "" : "s"}`}
                    className="ml-auto inline-flex h-[19px] min-w-[19px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground max-[860px]:ml-1"
                  >
                    {badge}
                  </span>
                )}
                {/* In the vertical rail the marker is pushed to the far edge; in
                    the horizontal strip it rides next to the label instead. */}
                {showDot && (
                  <span className={cn("size-[7px] rounded-full bg-primary shadow-[0_0_7px_var(--red-glow)] max-[860px]:ml-1", badge === 0 && "ml-auto")} />
                )}
              </button>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}
